import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Room, RoomEvent, Track } from 'livekit-client';
import api from '../../api';

interface Meeting {
  id: string;
  roomKey: string;
  title: string;
  description: string;
  hostName: string;
  scheduledStartAt: string;
  startedAt?: string;
  endedAt?: string;
  status: 'scheduled' | 'live' | 'ended' | 'cancelled';
  maxParticipants: number;
  recordingStatus: 'not_started' | 'recording' | 'processing' | 'complete' | 'failed';
  participantCount?: number;
  whiteboardEnabled?: boolean;
  screenShareAllowedParticipantIdentities?: string[];
  canManage: boolean;
}

interface MeetingRequest {
  id: string;
  roomKey: string;
  examType: 'WAEC' | 'JAMB';
  subjectName: string;
  studentName: string;
  studentEmail: string;
  title: string;
  details: string;
  preferredStartAt: string | null;
  status: 'pending' | 'approved' | 'declined' | 'scheduled';
  adminNote: string;
  studentMessage: string;
  coordinatorMessage: string;
  meetingId: string | null;
  createdAt: string;
}

interface WhiteboardShape {
  id: string;
  color: string;
  points: number[][];
  width?: number;
}

const upsertWhiteboardShape = (shapes: WhiteboardShape[], shape: WhiteboardShape) => {
  const shapeIndex = shapes.findIndex((current) => current.id === shape.id);
  if (shapeIndex === -1) return [...shapes, shape].slice(-2000);
  return shapes.map((current, index) => (index === shapeIndex ? shape : current));
};

interface VirtualMeetingsPanelProps {
  roomKey: string;
  subjectName: string;
  coordinator: boolean;
  allowStudentRequests: boolean;
  sessionsOnly?: boolean;
  requestDialogOpen?: boolean;
  onRequestDialogOpen?: () => void;
  onRequestDialogClose?: () => void;
}

const coordinatorConfig = (roomKey: string) => {
  const token = sessionStorage.getItem(`discussion-room-coordinator:${roomKey}`);
  return token ? { headers: { 'x-discussion-coordinator-token': token } } : undefined;
};

const endpoint = (roomKey: string) => `/discussion-rooms/${encodeURIComponent(roomKey)}/virtual-meetings`;

const avatarColors = ['#1565c0', '#6a1b9a', '#00897b', '#ef6c00', '#ad1457', '#2e7d32', '#5d4037', '#0277bd'];

const getAvatarColor = (identity: string) => {
  const hash = Array.from(identity).reduce((value, character) => (
    ((value * 31) + character.charCodeAt(0)) >>> 0
  ), 0);
  return avatarColors[hash % avatarColors.length];
};

const getInitials = (name: string) => name
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || '?';

const TrackVideo: React.FC<{
  track: Track;
  name: string;
  local?: boolean;
  featured?: boolean;
  coordinator?: boolean;
  tileWidth?: number;
}> = ({
  track,
  name,
  local = false,
  featured = false,
  coordinator = false,
  tileWidth,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return undefined;
    track.attach(element);
    return () => { track.detach(element); };
  }, [track]);
  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderRadius: 2,
        minWidth: 0,
        width: featured ? '100%' : `min(100%, ${tileWidth || (coordinator ? 460 : 240)}px)`,
      }}
    >
      <Box sx={{
        width: '100%',
        aspectRatio: featured ? undefined : '1 / 1',
        height: featured ? { xs: 'min(55vh, 480px)', md: 'min(72vh, 760px)' } : undefined,
        minHeight: featured ? 280 : undefined,
        maxHeight: featured ? 760 : undefined,
        bgcolor: 'grey.900',
      }}>
        <Box
          component="video"
          ref={videoRef}
          autoPlay
          playsInline
          muted={local}
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            bgcolor: 'grey.900',
            objectFit: featured ? 'contain' : 'cover',
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5 }}>{name}{local ? ' (you)' : ''}</Typography>
    </Paper>
  );
};

const TrackAudio: React.FC<{ track: Track; onPlaybackBlocked: () => void }> = ({ track, onPlaybackBlocked }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const element = audioRef.current;
    if (!element) return undefined;
    track.attach(element);
    element.play().catch(onPlaybackBlocked);
    return () => { track.detach(element); };
  }, [onPlaybackBlocked, track]);
  return <audio ref={audioRef} autoPlay />;
};

const CameraOffTile: React.FC<{ identity: string; name: string; local: boolean; coordinator: boolean }> = ({
  identity,
  name,
  local,
  coordinator,
}) => (
  <Paper
    variant="outlined"
    sx={{
      display: 'flex',
      flex: '1 1 160px',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      width: { xs: 'calc(50% - 8px)', sm: 190 },
      maxWidth: 220,
      minWidth: 0,
      aspectRatio: '1 / 1',
      borderRadius: 2,
      bgcolor: 'grey.900',
      color: 'common.white',
    }}
  >
    <Avatar
      aria-label={`${name} camera off`}
      sx={{
        width: { xs: 56, sm: 72 },
        height: { xs: 56, sm: 72 },
        bgcolor: getAvatarColor(identity),
        fontSize: { xs: '1.2rem', sm: '1.5rem' },
        fontWeight: 700,
      }}
    >
      {getInitials(name)}
    </Avatar>
    <Typography variant="caption" sx={{ px: 1, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {name}{local ? ' (you)' : ''}{coordinator ? ' · Coordinator' : ''}
    </Typography>
  </Paper>
);

const VirtualMeetingsPanel: React.FC<VirtualMeetingsPanelProps> = ({
  roomKey,
  subjectName,
  coordinator,
  allowStudentRequests,
  sessionsOnly = false,
  requestDialogOpen = false,
  onRequestDialogOpen,
  onRequestDialogClose,
}) => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [requests, setRequests] = useState<MeetingRequest[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [endingMeeting, setEndingMeeting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [requestTitle, setRequestTitle] = useState('');
  const [requestDetails, setRequestDetails] = useState('');
  const [preferredStartAt, setPreferredStartAt] = useState('');
  const [requestSaving, setRequestSaving] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState('');
  const [isCameraOn, setCameraOn] = useState(false);
  const [isMicrophoneOn, setMicrophoneOn] = useState(false);
  const [isScreenSharing, setScreenSharing] = useState(false);
  const [quality, setQuality] = useState('unknown');
  const [consented, setConsented] = useState(false);
  const [shapes, setShapes] = useState<WhiteboardShape[]>([]);
  const [chatDraft, setChatDraft] = useState('');
  const [chatMessages, setChatMessages] = useState<string[]>([]);
  const [recordingNotice, setRecordingNotice] = useState('');
  const [whiteboardEnabled, setWhiteboardEnabled] = useState(false);
  const [audioPlaybackBlocked, setAudioPlaybackBlocked] = useState(false);
  const [canShareScreen, setCanShareScreen] = useState(false);
  const [hostParticipantIdentity, setHostParticipantIdentity] = useState('');
  const [coordinatorParticipantIdentities, setCoordinatorParticipantIdentities] = useState<string[]>([]);
  const coordinatorParticipantIdentitiesRef = useRef<string[]>([]);
  const lastWhiteboardPublishAtRef = useRef(0);
  const [whiteboardTool, setWhiteboardTool] = useState<'pen' | 'eraser'>('pen');
  const [penColor, setPenColor] = useState('#1565c0');
  const [updatingSharePermission, setUpdatingSharePermission] = useState('');
  const [, setTrackRevision] = useState(0);
  const drawingRef = useRef<WhiteboardShape | null>(null);
  const shapesRef = useRef<WhiteboardShape[]>([]);
  const whiteboardRealtimeRevisionRef = useRef(0);
  const whiteboardEnabledRef = useRef(false);
  const meetingsLoadInFlightRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);
  const videoTracks = room ? [
    ...Array.from(room.localParticipant.trackPublications.values())
      .filter((publication) => publication.track?.kind === Track.Kind.Video)
      .map((publication) => ({
        track: publication.track as Track,
        name: room.localParticipant.name || 'You',
        participantIdentity: room.localParticipant.identity,
        local: true,
        screenShare: publication.source === Track.Source.ScreenShare,
      })),
    ...Array.from(room.remoteParticipants.values()).flatMap((participant) => (
      Array.from(participant.trackPublications.values())
        .filter((publication) => publication.track?.kind === Track.Kind.Video)
        .map((publication) => ({
          track: publication.track as Track,
          name: participant.name || 'Participant',
          participantIdentity: participant.identity,
          local: false,
          screenShare: publication.source === Track.Source.ScreenShare,
        }))
    )),
  ] : [];
  const screenShareTracks = videoTracks.filter((item) => item.screenShare);
  const cameraParticipants = room ? [
    {
      identity: room.localParticipant.identity,
      name: room.localParticipant.name || 'You',
      local: true,
      publication: Array.from(room.localParticipant.trackPublications.values()).find(
        (item) => item.kind === Track.Kind.Video && item.source === Track.Source.Camera
      ),
    },
    ...Array.from(room.remoteParticipants.values()).map((participant) => ({
      identity: participant.identity,
      name: participant.name || 'Participant',
      local: false,
      publication: Array.from(participant.trackPublications.values()).find(
        (item) => item.kind === Track.Kind.Video && item.source === Track.Source.Camera
      ),
    })),
  ] : [];
  const audioTracks = room ? Array.from(room.remoteParticipants.values()).flatMap((participant) => (
    Array.from(participant.trackPublications.values())
      .filter((publication) => publication.track?.kind === Track.Kind.Audio)
      .map((publication) => publication.track as Track)
  )) : [];
  const participantCount = room ? room.remoteParticipants.size + 1 : 0;
  const liveMeetingIds = meetings.filter((meeting) => meeting.status === 'live').map((meeting) => meeting.id).join(',');
  const handleAudioPlaybackBlocked = useCallback(() => setAudioPlaybackBlocked(true), []);

  const loadMeetings = useCallback(async () => {
    if (meetingsLoadInFlightRef.current) return;
    meetingsLoadInFlightRef.current = true;
    try {
      const response = await api.get(endpoint(roomKey), coordinatorConfig(roomKey));
      const currentMeetings: Meeting[] = Array.isArray(response.data?.data) ? response.data.data : [];
      setMeetings(currentMeetings);
      setSelectedMeeting((current) => {
        const latest = currentMeetings.find((meeting) => meeting.id === current?.id);
        return current && latest ? {
          ...current,
          canManage: latest.canManage,
          whiteboardEnabled: latest.whiteboardEnabled,
          recordingStatus: latest.recordingStatus,
          screenShareAllowedParticipantIdentities: latest.screenShareAllowedParticipantIdentities,
        } : current;
      });
      setError('');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to load meetings for this room.');
    } finally {
      meetingsLoadInFlightRef.current = false;
      setLoading(false);
    }
  }, [roomKey]);

  const loadRequests = useCallback(async () => {
    if (!allowStudentRequests && !coordinator) return;
    try {
      const response = await api.get(
        `/discussion-rooms/${encodeURIComponent(roomKey)}/virtual-meeting-requests`,
        coordinatorConfig(roomKey)
      );
      setRequests(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to load virtual session requests.');
    }
  }, [allowStudentRequests, coordinator, roomKey]);

  useEffect(() => {
    void loadMeetings();
    const timer = window.setInterval(() => { void loadMeetings(); }, 10000);
    return () => window.clearInterval(timer);
  }, [loadMeetings]);

  useEffect(() => {
    const refreshCounts = async () => {
      const counts = await Promise.all(liveMeetingIds.split(',').filter(Boolean).map(async (meetingId) => {
        try {
          const response = await api.get(`${endpoint(roomKey)}/${meetingId}/participants`, coordinatorConfig(roomKey));
          return {
            meetingId,
            count: Number(response.data?.data?.count) || 0,
            coordinatorParticipantIdentities: response.data?.data?.coordinatorParticipantIdentities,
          } as const;
        } catch (requestError: any) {
          setError(requestError?.response?.data?.message || 'Unable to refresh live participant counts.');
          return null;
        }
      }));
      const countById = new Map(counts
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .map((entry) => [entry.meetingId, entry.count] as const));
      if (countById.size) {
        setMeetings((current) => current.map((meeting) => (
          countById.has(meeting.id) ? { ...meeting, participantCount: countById.get(meeting.id) } : meeting
        )));
      }
      const selectedMeetingParticipants = counts.find((entry) => entry?.meetingId === selectedMeeting?.id);
      if (coordinator && selectedMeetingParticipants
        && Array.isArray(selectedMeetingParticipants.coordinatorParticipantIdentities)) {
        setCoordinatorParticipantIdentities(selectedMeetingParticipants.coordinatorParticipantIdentities);
        coordinatorParticipantIdentitiesRef.current = selectedMeetingParticipants.coordinatorParticipantIdentities;
      }
    };
    void refreshCounts();
    const timer = window.setInterval(() => { void refreshCounts(); }, 5000);
    return () => window.clearInterval(timer);
  }, [coordinator, liveMeetingIds, roomKey, selectedMeeting?.id]);

  useEffect(() => {
    void loadRequests();
    if (!allowStudentRequests && !coordinator) return undefined;
    const timer = window.setInterval(() => { void loadRequests(); }, 30000);
    return () => window.clearInterval(timer);
  }, [allowStudentRequests, coordinator, loadRequests]);

  useEffect(() => {
    shapesRef.current = shapes;
  }, [shapes]);

  useEffect(() => {
    if (!room || !selectedMeeting || selectedMeeting.canManage) return undefined;

    let active = true;
    let requestPending = false;
    let syncErrorReported = false;
    const synchronizeWhiteboard = async () => {
      if (!active || requestPending || drawingRef.current) return;
      requestPending = true;
      const realtimeRevision = whiteboardRealtimeRevisionRef.current;
      try {
        const response = await api.get(
          `${endpoint(roomKey)}/${selectedMeeting.id}/whiteboard`,
          coordinatorConfig(roomKey)
        );
        if (!active || drawingRef.current || realtimeRevision !== whiteboardRealtimeRevisionRef.current) return;
        const latestShapes: WhiteboardShape[] = Array.isArray(response.data?.data?.shapes)
          ? response.data.data.shapes
          : [];
        const currentShapes = shapesRef.current;
        const currentIds = currentShapes.map((shape) => shape.id);
        const latestIds = latestShapes.map((shape) => shape.id);
        const snapshotIsBehind = (currentIds.length > latestIds.length
          && latestIds.every((id, index) => id === currentIds[index]))
          || currentShapes.some((currentShape) => {
            const latestShape = latestShapes.find((shape) => shape.id === currentShape.id);
            return latestShape && currentShape.points.length > latestShape.points.length;
          });
        const shapesDiffer = latestShapes.length !== currentShapes.length
          || latestShapes.some((latestShape, index) => {
            const currentShape = currentShapes[index];
            return !currentShape
              || latestShape.id !== currentShape.id
              || latestShape.color !== currentShape.color
              || latestShape.width !== currentShape.width
              || latestShape.points.length !== currentShape.points.length
              || latestShape.points.some(([x, y], pointIndex) => {
                const currentPoint = currentShape.points[pointIndex];
                return !currentPoint || x !== currentPoint[0] || y !== currentPoint[1];
              });
          });

        if (!snapshotIsBehind && latestIds.length > 0 && shapesDiffer) {
          shapesRef.current = latestShapes;
          setShapes(latestShapes);
        }
        syncErrorReported = false;
      } catch (requestError: any) {
        if (active && !syncErrorReported) {
          setError(requestError?.response?.data?.message || 'Unable to synchronize the shared whiteboard.');
          syncErrorReported = true;
        }
      } finally {
        requestPending = false;
      }
    };

    const timer = window.setInterval(() => { void synchronizeWhiteboard(); }, 1000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [room, roomKey, selectedMeeting]);

  useEffect(() => () => {
    if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
    if (room) void room.disconnect();
  }, [room]);

  const saveWhiteboard = useCallback((next: WhiteboardShape[]) => {
    if (!selectedMeeting) return;
    setShapes(next);
    shapesRef.current = next;
    if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      void api.put(`${endpoint(roomKey)}/${selectedMeeting.id}/whiteboard`, { shapes: next }, coordinatorConfig(roomKey))
        .catch((requestError: any) => setError(requestError?.response?.data?.message || 'Unable to save whiteboard changes.'));
    }, 500);
  }, [roomKey, selectedMeeting]);

  const flushWhiteboard = async (meetingId: string) => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    await api.put(
      `${endpoint(roomKey)}/${meetingId}/whiteboard`,
      { shapes: shapesRef.current },
      coordinatorConfig(roomKey)
    );
  };

  const publish = useCallback(async (topic: string, value: unknown) => {
    if (!room) return;
    await room.localParticipant.publishData(
      new TextEncoder().encode(JSON.stringify(value)),
      { reliable: true, topic }
    );
  }, [room]);

  const connectMeeting = async (meeting: Meeting) => {
    setJoining(true);
    setError('');
    try {
      const [joinResponse, boardResponse] = await Promise.all([
        api.post(`${endpoint(roomKey)}/${meeting.id}/join`, {}, coordinatorConfig(roomKey)),
        api.get(`${endpoint(roomKey)}/${meeting.id}/whiteboard`, coordinatorConfig(roomKey)),
      ]);
      const data = joinResponse.data?.data;
      const liveRoom = new Room({ adaptiveStream: true, dynacast: true });
      liveRoom.on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
        if (!topic) return;
        try {
          const message = JSON.parse(new TextDecoder().decode(payload));
          if (topic === `a3-whiteboard:${meeting.id}`) {
            if (message?.action === 'coordinator' && typeof message.participantIdentity === 'string') {
              const nextIdentities = [...new Set([
                ...coordinatorParticipantIdentitiesRef.current,
                message.participantIdentity,
              ])];
              coordinatorParticipantIdentitiesRef.current = nextIdentities;
              setCoordinatorParticipantIdentities(nextIdentities);
            } else if (message?.action === 'access' && typeof message.enabled === 'boolean') {
              setWhiteboardEnabled(message.enabled);
              whiteboardEnabledRef.current = message.enabled;
              if (message.enabled) {
                void api.get(`${endpoint(roomKey)}/${meeting.id}/whiteboard`, coordinatorConfig(roomKey))
                  .then((response) => {
                    const next = Array.isArray(response.data?.data?.shapes) ? response.data.data.shapes : [];
                    setShapes(next);
                    shapesRef.current = next;
                  })
                  .catch((requestError: any) => setError(requestError?.response?.data?.message || 'Unable to load the shared whiteboard.'));
              }
            } else if (message?.action === 'clear' && (
              meeting.canManage
              || participant?.identity === data.hostParticipantIdentity
              || coordinatorParticipantIdentitiesRef.current.includes(participant?.identity || '')
            )) {
              whiteboardRealtimeRevisionRef.current += 1;
              setShapes([]);
              shapesRef.current = [];
            } else if (message?.shape && Array.isArray(message.shape.points) && (
              meeting.canManage
              || whiteboardEnabledRef.current
              || participant?.identity === data.hostParticipantIdentity
              || coordinatorParticipantIdentitiesRef.current.includes(participant?.identity || '')
            )) {
              whiteboardRealtimeRevisionRef.current += 1;
              const next = upsertWhiteboardShape(shapesRef.current, message.shape);
              shapesRef.current = next;
              setShapes(next);
            }
          } else if (topic === `a3-meeting-permissions:${meeting.id}` && (
            typeof message?.participantIdentity === 'string'
            && typeof message?.enabled === 'boolean'
          )) {
            if (message.enabled) {
              setSelectedMeeting((current) => current && current.canManage ? {
                ...current,
                screenShareAllowedParticipantIdentities: [
                  ...new Set([...(current.screenShareAllowedParticipantIdentities || []), message.participantIdentity]),
                ],
              } : current);
            } else {
              setSelectedMeeting((current) => current && current.canManage ? {
                ...current,
                screenShareAllowedParticipantIdentities: (current.screenShareAllowedParticipantIdentities || [])
                  .filter((identity) => identity !== message.participantIdentity),
              } : current);
            }
            if (message.participantIdentity === data.participantIdentity) setCanShareScreen(message.enabled);
            if (message.participantIdentity === data.participantIdentity
              && !message.enabled && liveRoom.localParticipant.isScreenShareEnabled) {
              void liveRoom.localParticipant.setScreenShareEnabled(false);
              setScreenSharing(false);
            }
          } else if (topic === `a3-meeting-chat:${meeting.id}` && typeof message?.text === 'string') {
            setChatMessages((current) => [...current.slice(-49), `${message.name || 'Participant'}: ${message.text}`]);
          } else if (topic === 'a3-meeting-recording' && message?.type === 'recording-consent-request') {
            setRecordingNotice('The coordinator has requested consent to record this session. Recording will only begin if everyone agrees.');
          }
        } catch {
          setError('A meeting update could not be read. Please refresh the meeting.');
        }
      });
      liveRoom.on(RoomEvent.ConnectionQualityChanged, (nextQuality, participant) => {
        if (participant.identity === data.participantIdentity) setQuality(String(nextQuality));
      });
      liveRoom.on(RoomEvent.TrackSubscribed, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.TrackUnsubscribed, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.TrackMuted, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.TrackUnmuted, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.LocalTrackPublished, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.LocalTrackUnpublished, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.ParticipantConnected, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.ParticipantDisconnected, () => setTrackRevision((current) => current + 1));
      liveRoom.on(RoomEvent.Disconnected, () => {
        setRoom(null);
        setSelectedMeeting(null);
        setMicrophoneOn(false);
        setCameraOn(false);
        setScreenSharing(false);
        setCanShareScreen(false);
        setHostParticipantIdentity('');
        setCoordinatorParticipantIdentities([]);
        coordinatorParticipantIdentitiesRef.current = [];
      });
      const initialShapes = Array.isArray(boardResponse.data?.data?.shapes) ? boardResponse.data.data.shapes : [];
      setShapes(initialShapes);
      shapesRef.current = initialShapes;
      const boardEnabled = Boolean(boardResponse.data?.data?.enabled);
      setWhiteboardEnabled(boardEnabled);
      whiteboardEnabledRef.current = boardEnabled;
      setCanShareScreen(Boolean(data.canShareScreen));
      setHostParticipantIdentity(data.hostParticipantIdentity || '');
      const coordinatorIdentities = Array.isArray(data.coordinatorParticipantIdentities)
        ? data.coordinatorParticipantIdentities
        : [];
      coordinatorParticipantIdentitiesRef.current = coordinatorIdentities;
      setCoordinatorParticipantIdentities(coordinatorIdentities);
      await liveRoom.connect(data.serverUrl, data.token, { autoSubscribe: true });
      setRoom(liveRoom);
      setSelectedMeeting({ ...meeting, ...(data.meeting || {}), recordingStatus: data.recordingStatus });
      setConsented(Boolean(data.consented));
      setAudioPlaybackBlocked(false);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to join this meeting.');
    } finally {
      setJoining(false);
    }
  };

  const createMeeting = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        ...(scheduledAt ? { scheduledStartAt: new Date(scheduledAt).toISOString() } : {}),
        ...(selectedRequestId ? { requestId: selectedRequestId } : {}),
      };
      if (editingMeeting) {
        await api.patch(`${endpoint(roomKey)}/${editingMeeting.id}`, payload, coordinatorConfig(roomKey));
        setEditingMeeting(null);
      } else {
        await api.post(endpoint(roomKey), payload, coordinatorConfig(roomKey));
      }
      setTitle('');
      setDescription('');
      setScheduledAt('');
      setSelectedRequestId('');
      await loadMeetings();
      await loadRequests();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to schedule this meeting.');
    } finally {
      setSaving(false);
    }
  };

  const submitRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!requestTitle.trim() || requestSaving) return;
    setRequestSaving(true);
    setError('');
    setRequestSuccess('');
    try {
      await api.post(
        `/discussion-rooms/${encodeURIComponent(roomKey)}/virtual-meeting-requests`,
        {
          title: requestTitle.trim(),
          details: requestDetails.trim(),
          ...(preferredStartAt ? { preferredStartAt: new Date(preferredStartAt).toISOString() } : {}),
        }
      );
      setRequestTitle('');
      setRequestDetails('');
      setPreferredStartAt('');
      setRequestSuccess('Your request has been submitted. Check this room for its review status and any scheduling updates.');
      await loadRequests();
      onRequestDialogClose?.();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to submit your virtual session request.');
    } finally {
      setRequestSaving(false);
    }
  };

  const prepareMeetingFromRequest = (request: MeetingRequest) => {
    setSelectedRequestId(request.id);
    setTitle(request.title);
    setDescription(request.details.slice(0, 1000));
    if (request.preferredStartAt) {
      const date = new Date(request.preferredStartAt);
      setScheduledAt(new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const editMeeting = (meeting: Meeting) => {
    const date = new Date(meeting.scheduledStartAt);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setEditingMeeting(meeting);
    setTitle(meeting.title);
    setDescription(meeting.description);
    setScheduledAt(localDate);
  };

  const cancelEdit = () => {
    setEditingMeeting(null);
    setTitle('');
    setDescription('');
    setScheduledAt('');
  };

  const manageMeeting = async (meeting: Meeting, action: 'start' | 'cancel' | 'end') => {
    setError('');
    try {
      await api.post(`${endpoint(roomKey)}/${meeting.id}/${action}`, {}, coordinatorConfig(roomKey));
      await loadMeetings();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || `Unable to ${action} this meeting.`);
    }
  };

  const setRecordingConsent = async (consent: boolean) => {
    if (!selectedMeeting) return;
    try {
      const response = await api.put(`${endpoint(roomKey)}/${selectedMeeting.id}/recording/consent`, { consent }, coordinatorConfig(roomKey));
      setConsented(consent);
      setSelectedMeeting({ ...selectedMeeting, recordingStatus: response.data?.data?.recordingStatus || selectedMeeting.recordingStatus });
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to update recording consent.');
    }
  };

  const manageRecording = async (action: 'start' | 'stop') => {
    if (!selectedMeeting) return;
    setError('');
    if (action === 'start') setRecordingNotice('The coordinator is requesting consent to record this session. Recording will only start after every participant agrees.');
    try {
      const response = await api.post(`${endpoint(roomKey)}/${selectedMeeting.id}/recording/${action}`, {}, coordinatorConfig(roomKey));
      setSelectedMeeting({ ...selectedMeeting, recordingStatus: response.data?.data?.recordingStatus || selectedMeeting.recordingStatus });
      await loadMeetings();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || `Unable to ${action} recording.`);
    }
  };

  const deleteRecording = async (meeting: Meeting) => {
    if (!window.confirm(`Permanently delete the recording for "${meeting.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`${endpoint(roomKey)}/${meeting.id}/recording`, coordinatorConfig(roomKey));
      await loadMeetings();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to delete this recording.');
    }
  };

  const leaveMeeting = async () => {
    if (room) await room.disconnect();
    setRoom(null);
    setSelectedMeeting(null);
    setMicrophoneOn(false);
    setCameraOn(false);
    setScreenSharing(false);
    setCanShareScreen(false);
    setHostParticipantIdentity('');
    setCoordinatorParticipantIdentities([]);
    coordinatorParticipantIdentitiesRef.current = [];
    setChatMessages([]);
    await loadMeetings();
  };

  const endMeetingForAll = async () => {
    if (!selectedMeeting || !room) return;
    if (!window.confirm('End this meeting for every participant?')) return;
    setEndingMeeting(true);
    try {
      await api.post(`${endpoint(roomKey)}/${selectedMeeting.id}/end`, {}, coordinatorConfig(roomKey));
      await room.disconnect();
      setRoom(null);
      setSelectedMeeting(null);
      setMicrophoneOn(false);
      setCameraOn(false);
      setScreenSharing(false);
      setCanShareScreen(false);
      setHostParticipantIdentity('');
      setCoordinatorParticipantIdentities([]);
      coordinatorParticipantIdentitiesRef.current = [];
      setChatMessages([]);
      await loadMeetings();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to end this meeting for everyone.');
    } finally {
      setEndingMeeting(false);
    }
  };

  const sendChat = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = chatDraft.trim();
    if (!text || !selectedMeeting) return;
    try {
      await publish(`a3-meeting-chat:${selectedMeeting.id}`, { text, name: room?.localParticipant.name || 'Participant' });
      setChatMessages((current) => [...current.slice(-49), `You: ${text}`]);
      setChatDraft('');
    } catch {
      setError('Unable to send meeting message.');
    }
  };

  const changeMedia = async (type: 'camera' | 'microphone' | 'screen') => {
    if (!room) return;
    const previousValue = type === 'camera' ? isCameraOn : type === 'microphone' ? isMicrophoneOn : isScreenSharing;
    const nextValue = !previousValue;
    if (type === 'camera') setCameraOn(nextValue);
    else if (type === 'microphone') setMicrophoneOn(nextValue);
    else setScreenSharing(nextValue);
    try {
      if (type === 'camera') {
        await room.localParticipant.setCameraEnabled(nextValue);
      } else if (type === 'microphone') {
        await room.localParticipant.setMicrophoneEnabled(nextValue);
      } else {
        if (!selectedMeeting?.canManage && !canShareScreen) {
          setScreenSharing(previousValue);
          setError('The coordinator has not enabled screen sharing for participants.');
          return;
        }
        await room.localParticipant.setScreenShareEnabled(nextValue);
      }
    } catch {
      if (type === 'camera') setCameraOn(previousValue);
      else if (type === 'microphone') setMicrophoneOn(previousValue);
      else setScreenSharing(previousValue);
      setError(`Unable to change ${type} settings. Check browser permissions and try again.`);
    }
  };

  const drawWhiteboard = (event: React.PointerEvent<SVGSVGElement>, finish = false) => {
    if (!room || !selectedMeeting || (!selectedMeeting.canManage && !whiteboardEnabled)) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const point = [((event.clientX - bounds.left) / bounds.width) * 1000, ((event.clientY - bounds.top) / bounds.height) * 500];
    if (finish && drawingRef.current) {
      const shape = {
        ...drawingRef.current,
        points: [...drawingRef.current.points, point].slice(-300),
      };
      drawingRef.current = null;
      const next = upsertWhiteboardShape(shapesRef.current, shape);
      saveWhiteboard(next);
      void flushWhiteboard(selectedMeeting.id)
        .catch((requestError: any) => setError(requestError?.response?.data?.message || 'Unable to save whiteboard changes.'));
      void publish(`a3-whiteboard:${selectedMeeting.id}`, { shape })
        .catch((requestError: any) => setError(requestError?.response?.data?.message || 'Unable to share whiteboard changes live.'));
      return;
    }
    if (!drawingRef.current) {
      drawingRef.current = {
        id: crypto.randomUUID(),
        color: whiteboardTool === 'eraser' ? '#ffffff' : penColor,
        width: whiteboardTool === 'eraser' ? 28 : 4,
        points: [point],
      };
      const next = upsertWhiteboardShape(shapesRef.current, drawingRef.current);
      shapesRef.current = next;
      setShapes(next);
      whiteboardRealtimeRevisionRef.current += 1;
      lastWhiteboardPublishAtRef.current = Date.now();
      void publish(`a3-whiteboard:${selectedMeeting.id}`, { shape: drawingRef.current })
        .catch((requestError: any) => setError(requestError?.response?.data?.message || 'Unable to share whiteboard changes live.'));
    } else {
      drawingRef.current = { ...drawingRef.current, points: [...drawingRef.current.points, point].slice(-300) };
      const next = upsertWhiteboardShape(shapesRef.current, drawingRef.current);
      shapesRef.current = next;
      setShapes(next);
      const now = Date.now();
      if (now - lastWhiteboardPublishAtRef.current >= 50) {
        lastWhiteboardPublishAtRef.current = now;
        void publish(`a3-whiteboard:${selectedMeeting.id}`, { shape: drawingRef.current })
          .catch((requestError: any) => setError(requestError?.response?.data?.message || 'Unable to share whiteboard changes live.'));
      }
    }
  };

  const clearWhiteboard = async () => {
    if (!selectedMeeting || !room || !selectedMeeting.canManage) return;
    saveWhiteboard([]);
    try {
      await flushWhiteboard(selectedMeeting.id);
      await publish(`a3-whiteboard:${selectedMeeting.id}`, { action: 'clear' });
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to clear the shared whiteboard.');
    }
  };

  const toggleWhiteboardAccess = async () => {
    if (!selectedMeeting || !selectedMeeting.canManage) return;
    const previousValue = whiteboardEnabled;
    const enabled = !previousValue;
    setWhiteboardEnabled(enabled);
    whiteboardEnabledRef.current = enabled;
    try {
      await flushWhiteboard(selectedMeeting.id);
      const response = await api.patch(
        `${endpoint(roomKey)}/${selectedMeeting.id}/whiteboard/access`,
        { enabled },
        coordinatorConfig(roomKey)
      );
      const savedValue = Boolean(response.data?.data?.enabled);
      setWhiteboardEnabled(savedValue);
      whiteboardEnabledRef.current = savedValue;
    } catch (requestError: any) {
      setWhiteboardEnabled(previousValue);
      whiteboardEnabledRef.current = previousValue;
      setError(requestError?.response?.data?.message || 'Unable to update whiteboard access.');
    }
  };

  const setParticipantScreenShareAccess = async (participantIdentity: string, enabled: boolean) => {
    if (!selectedMeeting?.canManage) return;
    const previousIdentities = selectedMeeting.screenShareAllowedParticipantIdentities || [];
    const nextIdentities = enabled
      ? [participantIdentity]
      : previousIdentities.filter((identity) => identity !== participantIdentity);
    setSelectedMeeting({ ...selectedMeeting, screenShareAllowedParticipantIdentities: nextIdentities });
    setUpdatingSharePermission(participantIdentity);
    try {
      await api.patch(
        `${endpoint(roomKey)}/${selectedMeeting.id}/screen-share/access`,
        { participantIdentity, enabled },
        coordinatorConfig(roomKey)
      );
    } catch (requestError: any) {
      setSelectedMeeting((current) => current && current.id === selectedMeeting.id
        ? { ...current, screenShareAllowedParticipantIdentities: previousIdentities }
        : current);
      setError(requestError?.response?.data?.message || 'Unable to update screen-sharing access.');
    } finally {
      setUpdatingSharePermission('');
    }
  };

  const openPlayback = async (meeting: Meeting) => {
    try {
      const response = await api.get(`${endpoint(roomKey)}/${meeting.id}/recording/playback`, coordinatorConfig(roomKey));
      window.open(response.data.data.url, '_blank', 'noopener,noreferrer');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to open this recording.');
    }
  };

  const downloadAttendance = async (meeting: Meeting) => {
    try {
      const response = await api.get(
        `${endpoint(roomKey)}/${meeting.id}/attendance`,
        { ...coordinatorConfig(roomKey), responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `virtual-meeting-attendance-${meeting.id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to download meeting attendance.');
    }
  };

  if (sessionsOnly && !(selectedMeeting && room)) {
    const visibleMeetings = meetings.filter((meeting) => ['live', 'scheduled'].includes(meeting.status));
    return (
      <Stack spacing={1.5} sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
          <Box>
            <Typography variant="h6">Virtual sessions</Typography>
            <Typography variant="body2" color="text.secondary">Join a live session here, or check its scheduled start time.</Typography>
          </Box>
          {allowStudentRequests && <Button variant="outlined" onClick={onRequestDialogOpen}>Request live session</Button>}
        </Stack>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {requestSuccess && <Alert severity="success" onClose={() => setRequestSuccess('')}>{requestSuccess}</Alert>}
        {requests.length > 0 && (
          <Stack spacing={1}>
            {requests.map((request) => (
              <Paper key={request.id} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{request.title}</Typography>
                  <Chip size="small" label={request.status} color={request.status === 'scheduled' ? 'success' : request.status === 'declined' ? 'default' : 'info'} />
                </Stack>
                {(request.studentMessage || request.adminNote) && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Admin message: {request.studentMessage || request.adminNote}</Typography>}
              </Paper>
            ))}
          </Stack>
        )}
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={26} /></Box> : visibleMeetings.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography color="text.secondary">No live or upcoming virtual sessions in this room.</Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {visibleMeetings.map((meeting) => (
              <Paper key={meeting.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5} alignItems={{ sm: 'center' }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{meeting.title}</Typography>
                      <Chip size="small" label={meeting.status} color={meeting.status === 'live' ? 'success' : 'default'} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {meeting.status === 'live'
                        ? `${meeting.participantCount ?? 0} participant${meeting.participantCount === 1 ? '' : 's'} now`
                        : `Starts ${new Date(meeting.scheduledStartAt).toLocaleString()}`}
                    </Typography>
                  </Box>
                  {meeting.status === 'live' && <Button variant="contained" disabled={joining} onClick={() => void connectMeeting(meeting)}>{joining ? 'Joining…' : 'Join meeting'}</Button>}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
        <Dialog open={requestDialogOpen} onClose={onRequestDialogClose} fullWidth maxWidth="sm">
          <DialogTitle>Request a virtual session</DialogTitle>
          <Box component="form" onSubmit={submitRequest}>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tell the admin what help you need. The room coordinator manages and schedules approved sessions.
              </Typography>
              <Stack spacing={1.5}>
                <TextField required autoFocus label="Session topic" value={requestTitle} onChange={(event) => setRequestTitle(event.target.value)} inputProps={{ maxLength: 120 }} />
                <TextField label="What would you like help with?" value={requestDetails} onChange={(event) => setRequestDetails(event.target.value)} inputProps={{ maxLength: 1200 }} multiline minRows={2} />
                <TextField label="Preferred date and time (optional)" type="datetime-local" value={preferredStartAt} onChange={(event) => setPreferredStartAt(event.target.value)} InputLabelProps={{ shrink: true }} />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onRequestDialogClose}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={requestSaving}>{requestSaving ? 'Submitting…' : 'Send request'}</Button>
            </DialogActions>
          </Box>
        </Dialog>
      </Stack>
    );
  }

  if (selectedMeeting && room) {
    return (
      <Stack spacing={2} sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
          <Box>
            <Typography variant="h6">{selectedMeeting.title}</Typography>
            <Typography variant="caption" color="text.secondary">{subjectName} · connection {quality}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color="success" label={`${participantCount} in meeting`} />
            {selectedMeeting.recordingStatus === 'recording' && <Chip color="error" label="Recording" />}
            <Button onClick={() => void leaveMeeting()}>Leave meeting</Button>
            {selectedMeeting.canManage && (
              <Button variant="contained" color="error" disabled={endingMeeting} onClick={() => void endMeetingForAll()}>
                {endingMeeting ? 'Ending meeting…' : 'End Meeting'}
              </Button>
            )}
          </Stack>
        </Stack>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {recordingNotice && <Alert severity="info" onClose={() => setRecordingNotice('')}>{recordingNotice}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(280px, 360px)' }, gap: 2 }}>
          <Stack spacing={2}>
            {screenShareTracks.length > 0 && (
              <Box sx={{ width: '100%' }}>
                <TrackVideo key={`screen-${screenShareTracks[0].name}`} {...screenShareTracks[0]} featured />
              </Box>
            )}
            {cameraParticipants.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Participants</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5 }}>
                  {cameraParticipants.map((participant) => {
                    const isCoordinator = participant.identity === hostParticipantIdentity
                      || coordinatorParticipantIdentities.includes(participant.identity)
                      || (coordinator && participant.local);
                    const cameraTrack = participant.publication && !participant.publication.isMuted
                      ? participant.publication.track
                      : undefined;
                    return cameraTrack ? (
                      <Box
                        key={participant.identity}
                        sx={{
                          width: { xs: 'calc(50% - 8px)', sm: isCoordinator ? 230 : 190 },
                          maxWidth: isCoordinator ? 260 : 220,
                          flex: '1 1 160px',
                        }}
                      >
                        <TrackVideo
                          track={cameraTrack}
                          name={`${participant.name}${isCoordinator ? ' · Coordinator' : ''}`}
                          local={participant.local}
                          coordinator={isCoordinator}
                          tileWidth={isCoordinator ? 240 : 200}
                        />
                      </Box>
                    ) : (
                      <CameraOffTile
                        key={participant.identity}
                        identity={participant.identity}
                        name={participant.name}
                        local={participant.local}
                        coordinator={isCoordinator}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
            {screenShareTracks.slice(1).map((item, index) => <TrackVideo key={`screen-${item.name}-${index}`} {...item} />)}
            {cameraParticipants.length === 0 && <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Participants will appear here when they join.</Typography></Paper>}
            {audioTracks.map((track, index) => <TrackAudio key={`audio-${index}`} track={track} onPlaybackBlocked={handleAudioPlaybackBlocked} />)}
            {audioPlaybackBlocked && (
              <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => {
                void room.startAudio().then(() => setAudioPlaybackBlocked(false)).catch(() => setError('Meeting audio could not be started. Check browser audio permissions.'));
              }}>Enable audio</Button>}>
                Your browser blocked participant audio. Select Enable audio to hear the meeting.
              </Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant={isMicrophoneOn ? 'contained' : 'outlined'}
                startIcon={isMicrophoneOn ? <MicIcon /> : <MicOffIcon />}
                onClick={() => void changeMedia('microphone')}
              >
                {isMicrophoneOn ? 'Mute microphone' : 'Enable microphone'}
              </Button>
              <Button
                variant={isCameraOn ? 'contained' : 'outlined'}
                startIcon={isCameraOn ? <VideocamIcon /> : <VideocamOffIcon />}
                onClick={() => void changeMedia('camera')}
              >
                {isCameraOn ? 'Turn camera off' : 'Turn camera on'}
              </Button>
              <Button
                variant={isScreenSharing ? 'contained' : 'outlined'}
                disabled={!selectedMeeting.canManage && !canShareScreen}
                startIcon={isScreenSharing
                  ? <StopScreenShareIcon />
                  : canShareScreen || selectedMeeting.canManage
                    ? <ScreenShareIcon />
                    : <LockOutlinedIcon />}
                onClick={() => void changeMedia('screen')}
              >
                {isScreenSharing ? 'Stop sharing' : canShareScreen || selectedMeeting.canManage ? 'Share screen' : 'Screen sharing locked'}
              </Button>
            </Stack>
            {selectedMeeting.canManage && room && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2">Student screen-sharing permissions</Typography>
                <Typography variant="caption" color="text.secondary">
                  Only one student may share at a time. Granting access to another student revokes the previous grant.
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {Array.from(room.remoteParticipants.values())
                    .filter((participant) => !coordinatorParticipantIdentities.includes(participant.identity))
                    .map((participant) => {
                      const allowed = (selectedMeeting.screenShareAllowedParticipantIdentities || []).includes(participant.identity);
                      return (
                        <Stack key={participant.identity} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                          <Typography variant="body2">{participant.name || 'Student'}</Typography>
                          <Button
                            size="small"
                            variant={allowed ? 'outlined' : 'contained'}
                            disabled={Boolean(updatingSharePermission)}
                            onClick={() => void setParticipantScreenShareAccess(participant.identity, !allowed)}
                          >
                            {updatingSharePermission === participant.identity
                              ? 'Updating…'
                              : allowed ? 'Revoke screen share' : 'Allow screen share'}
                          </Button>
                        </Stack>
                      );
                    })}
                  {!Array.from(room.remoteParticipants.values()).some((participant) => !coordinatorParticipantIdentities.includes(participant.identity))
                    && <Typography variant="body2" color="text.secondary">Students will appear here when they join.</Typography>}
                </Stack>
              </Paper>
            )}
            {selectedMeeting.recordingStatus !== 'recording' && !consented && (
              <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => void setRecordingConsent(true)}>I consent</Button>}>
                This lesson may be recorded. Consent is optional; recording can only start after everyone agrees.
              </Alert>
            )}
            {consented && (
              <Alert severity={selectedMeeting.recordingStatus === 'recording' ? 'warning' : 'success'} action={<Button color="inherit" size="small" onClick={() => void setRecordingConsent(false)}>Withdraw</Button>}>
                You consented to recording. You can withdraw consent at any time{selectedMeeting.recordingStatus === 'recording' ? ', and recording will stop.' : '.'}
              </Alert>
            )}
            {selectedMeeting.canManage && (
              <Button color={selectedMeeting.recordingStatus === 'recording' ? 'error' : 'primary'} variant="contained" onClick={() => void manageRecording(selectedMeeting.recordingStatus === 'recording' ? 'stop' : 'start')}>
                {selectedMeeting.recordingStatus === 'recording' ? 'Stop recording' : 'Start recording'}
              </Button>
            )}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Shared whiteboard</Typography>
                <Stack direction="row" spacing={1}>
                  {selectedMeeting.canManage && <Button size="small" onClick={() => void toggleWhiteboardAccess()}>{whiteboardEnabled ? 'Disable student drawing' : 'Allow student drawing'}</Button>}
                  {selectedMeeting.canManage && <Button size="small" onClick={() => void clearWhiteboard()}>Clear board</Button>}
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="whiteboard-tool-label">Tool</InputLabel>
                  <Select
                    labelId="whiteboard-tool-label"
                    value={whiteboardTool}
                    label="Tool"
                    disabled={!selectedMeeting.canManage && !whiteboardEnabled}
                    onChange={(event) => setWhiteboardTool(event.target.value as 'pen' | 'eraser')}
                  >
                    <MenuItem value="pen">Pen</MenuItem>
                    <MenuItem value="eraser">Eraser</MenuItem>
                  </Select>
                </FormControl>
                {whiteboardTool === 'pen' && (
                  <TextField
                    size="small"
                    type="color"
                    label="Pen ink"
                    value={penColor}
                    disabled={!selectedMeeting.canManage && !whiteboardEnabled}
                    onChange={(event) => setPenColor(event.target.value)}
                    sx={{ width: 110 }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Stack>
              <Box
                component="svg"
                viewBox="0 0 1000 500"
                preserveAspectRatio="none"
                onPointerDown={(event: React.PointerEvent<SVGSVGElement>) => {
                  if (selectedMeeting.canManage || whiteboardEnabled) {
                    event.currentTarget.setPointerCapture(event.pointerId);
                    drawingRef.current = null;
                    drawWhiteboard(event);
                  }
                }}
                onPointerMove={(event: React.PointerEvent<SVGSVGElement>) => { if (event.buttons === 1) drawWhiteboard(event); }}
                onPointerUp={(event: React.PointerEvent<SVGSVGElement>) => drawWhiteboard(event, true)}
                sx={{ width: '100%', height: { xs: 240, md: 360 }, border: 1, borderColor: 'divider', borderRadius: 1, touchAction: (selectedMeeting.canManage || whiteboardEnabled) ? 'none' : 'auto', bgcolor: '#ffffff', cursor: (selectedMeeting.canManage || whiteboardEnabled) ? 'crosshair' : 'default' }}
              >
                {shapes.map((shape) => (
                  <polyline
                    key={shape.id}
                    points={shape.points.map(([x, y]) => `${x},${y}`).join(' ')}
                    fill="none"
                    stroke={shape.color}
                    strokeWidth={shape.width || 4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {coordinator
                  ? whiteboardEnabled
                    ? 'Participants can draw and write on the board; changes sync live.'
                    : selectedMeeting.canManage
                      ? 'Only you can draw; students can view the board. Allow student drawing when ready.'
                      : 'Student drawing is currently disabled. You can enable participant drawing when ready.'
                  : whiteboardEnabled
                    ? 'Students can draw and write on the board; changes sync live.'
                    : 'You can view the coordinator’s board. Student drawing is currently disabled.'}
              </Typography>
            </Paper>
          </Stack>
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', minHeight: 300, maxHeight: 600 }}>
            <Typography variant="subtitle1">Meeting chat</Typography>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={1} sx={{ flex: 1, overflowY: 'auto', mb: 1 }}>
              {chatMessages.map((message, index) => <Typography key={`${index}-${message}`} variant="body2" sx={{ overflowWrap: 'anywhere' }}>{message}</Typography>)}
            </Stack>
            <Box component="form" onSubmit={sendChat} sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" fullWidth value={chatDraft} onChange={(event) => setChatDraft(event.target.value)} inputProps={{ maxLength: 500 }} label="Message" />
              <Button type="submit" disabled={!chatDraft.trim()}>Send</Button>
            </Box>
          </Paper>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Box>
        <Typography variant="h6">Virtual meetings</Typography>
        <Typography variant="body2" color="text.secondary">{subjectName} · meetings and lessons for this discussion room.</Typography>
      </Box>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {requestSuccess && <Alert severity="success" onClose={() => setRequestSuccess('')}>{requestSuccess}</Alert>}
      {allowStudentRequests && (
        <Paper id="virtual-session-request" component="form" variant="outlined" onSubmit={submitRequest} sx={{ p: 2, scrollMarginTop: 24 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Request a virtual session</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Tell the admin what you need in this subject room. An approved request is passed to the room coordinator to schedule.
          </Typography>
          <Stack spacing={1.5}>
            <TextField required label="Session topic" value={requestTitle} onChange={(event) => setRequestTitle(event.target.value)} inputProps={{ maxLength: 120 }} />
            <TextField label="What would you like help with?" value={requestDetails} onChange={(event) => setRequestDetails(event.target.value)} inputProps={{ maxLength: 1200 }} multiline minRows={2} />
            <TextField label="Preferred date and time (optional)" type="datetime-local" value={preferredStartAt} onChange={(event) => setPreferredStartAt(event.target.value)} InputLabelProps={{ shrink: true }} />
            <Button type="submit" variant="contained" disabled={requestSaving}>{requestSaving ? 'Submitting…' : 'Request a session'}</Button>
          </Stack>
          {requests.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Your requests</Typography>
              <Stack spacing={1}>
                {requests.map((request) => (
                  <Paper key={request.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{request.title}</Typography>
                      <Chip size="small" label={request.status} color={request.status === 'scheduled' ? 'success' : request.status === 'declined' ? 'default' : 'info'} />
                    </Stack>
                    {(request.studentMessage || request.adminNote) && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Admin message: {request.studentMessage || request.adminNote}</Typography>}
                    {request.status === 'scheduled' && <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>The coordinator scheduled a meeting. Check the meetings list above.</Typography>}
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      )}
      {coordinator && (
        <Paper component="form" variant="outlined" onSubmit={createMeeting} sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{editingMeeting ? 'Edit scheduled meeting' : 'Schedule or start a meeting'}</Typography>
          <Stack spacing={1.5}>
            <TextField required label="Meeting title" value={title} onChange={(event) => setTitle(event.target.value)} inputProps={{ maxLength: 120 }} />
            <TextField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} inputProps={{ maxLength: 1000 }} multiline minRows={2} />
            <TextField label="Scheduled start (leave blank to start now)" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} InputLabelProps={{ shrink: true }} />
            {selectedRequestId && <Alert severity="info">This meeting will be linked to the approved student request.</Alert>}
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving…' : editingMeeting ? 'Save changes' : 'Create meeting'}</Button>
              {editingMeeting && <Button onClick={cancelEdit} disabled={saving}>Cancel edit</Button>}
            </Stack>
          </Stack>
        </Paper>
      )}
      {coordinator && requests.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Student session requests</Typography>
          <Stack spacing={1}>
            {requests.map((request) => (
              <Paper key={request.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} alignItems={{ sm: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2">{request.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{request.studentName} · {request.details || 'No additional details'}</Typography>
                    {request.preferredStartAt && <Typography variant="caption" color="text.secondary">Preferred: {new Date(request.preferredStartAt).toLocaleString()}</Typography>}
                  </Box>
                  {request.status === 'approved' && <Button size="small" onClick={() => prepareMeetingFromRequest(request)}>Schedule this request</Button>}
                  {request.status === 'pending' && <Chip size="small" label="Awaiting admin review" />}
                </Stack>
                {request.coordinatorMessage && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Admin message for coordinator: {request.coordinatorMessage}</Typography>}
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box> : meetings.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{coordinator ? 'No meetings scheduled. Create one for this room.' : 'There are no meetings scheduled for this room yet.'}</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {meetings.map((meeting) => (
            <Paper key={meeting.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{meeting.title}</Typography>
                    <Chip size="small" label={meeting.status} color={meeting.status === 'live' ? 'success' : 'default'} />
                    {meeting.recordingStatus === 'recording' && <Chip size="small" color="error" label="Recording" />}
                    {meeting.recordingStatus === 'complete' && <Chip size="small" color="info" label="Recording ready" />}
                  </Stack>
                  {meeting.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{meeting.description}</Typography>}
                  <Typography variant="caption" color="text.secondary">{new Date(meeting.scheduledStartAt).toLocaleString()} · Coordinator: {meeting.hostName} · Max {meeting.maxParticipants}</Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  {meeting.status === 'live' && <Button variant="contained" disabled={joining} onClick={() => void connectMeeting(meeting)}>{joining ? 'Joining…' : 'Join'}</Button>}
                  {meeting.canManage && meeting.status === 'scheduled' && <Button onClick={() => void manageMeeting(meeting, 'start')}>Start</Button>}
                  {meeting.canManage && meeting.status === 'scheduled' && <Button onClick={() => editMeeting(meeting)}>Edit</Button>}
                  {meeting.canManage && ['scheduled', 'live'].includes(meeting.status) && <Button color="error" onClick={() => void manageMeeting(meeting, meeting.status === 'live' ? 'end' : 'cancel')}>{meeting.status === 'live' ? 'End' : 'Cancel'}</Button>}
                  {meeting.canManage && <Button onClick={() => void downloadAttendance(meeting)}>Download attendance</Button>}
                  {meeting.recordingStatus === 'complete' && <Button onClick={() => void openPlayback(meeting)}>Watch recording</Button>}
                  {meeting.canManage && ['complete', 'failed'].includes(meeting.recordingStatus) && (
                    <Button color="error" onClick={() => void deleteRecording(meeting)}>Delete recording</Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default VirtualMeetingsPanel;
